// frontend/src/types/livestock.ts

import type { PhotoFraming } from './photos';

export type LivestockStatus = 'alive' | 'deceased' | 'sold' | 'unknown';
export type LivestockType = 'fish' | 'coral' | 'invertebrate' | 'plant';
export type Currency = 'USD' | 'GBP' | 'EUR' | 'AUD' | 'CAD';

export interface Livestock {
  id: number;
  tank_id: string;
  livestock_type: LivestockType;
  fish_species_id: number | null;
  common_name: string;
  scientific_name: string | null;
  category: string | null;
  subcategory: string | null;
  acquisition_date: string;
  deceased_date: string | null;
  status: LivestockStatus;
  quantity: number;
  vendor: string | null;
  price_paid: number | null;
  currency: Currency;
  tank_bred: boolean;
  notes: string | null;
  thumbnail_url: string | null;
  /** The organism's reference photo, a timeline entry: others are lined up to it. */
  reference_entry_id?: number | null;
  /** Signed URL of the reference's display variant (the detail read only). */
  reference_display_url?: string | null;
  /** The reference's own framing (the detail read only). */
  reference_framing?: PhotoFraming | null;
  created_at: string;
  updated_at: string;
}

export interface LivestockCreate {
  livestock_type?: LivestockType;
  fish_species_id?: number | null;
  common_name: string;
  scientific_name?: string | null;
  category?: string | null;
  subcategory?: string | null;
  acquisition_date: string;
  quantity?: number;
  vendor?: string | null;
  price_paid?: number | null;
  currency?: Currency;
  tank_bred?: boolean;
  notes?: string | null;
}

export interface LivestockUpdate {
  livestock_type?: LivestockType;
  common_name?: string;
  scientific_name?: string | null;
  category?: string | null;
  subcategory?: string | null;
  acquisition_date?: string;
  deceased_date?: string | null;
  status?: LivestockStatus;
  quantity?: number;
  vendor?: string | null;
  price_paid?: number | null;
  currency?: Currency;
  tank_bred?: boolean;
  notes?: string | null;
}

export interface LivestockStatusUpdate {
  status: LivestockStatus;
  deceased_date?: string | null;
  notes?: string | null;
}

export interface LivestockListResponse {
  livestock: Livestock[];
  total: number;
}

export interface LivestockSummary {
  total_count: number;
  alive_count: number;
  deceased_count: number;
  total_value: number | null;
  currency: Currency;
  recent_additions: number;
  recent_losses: number;
}

export interface FishSpeciesSearchResult {
  id: number;
  common_name: string;
  scientific_name: string | null;
  family: string | null;
  category: string | null;
  care_level: string | null;
  temperament: string | null;
  reef_compatible: string | null;
  max_size_inches: number | null;
  min_tank_size_gallons: number | null;
  image_url: string | null;
  match_score: number;
}

export interface FishSpeciesSearchResponse {
  results: FishSpeciesSearchResult[];
  total_count: number;
  query: string;
}

export interface FishCategoryItem {
  name: string;
  count: number;
}

export interface FishCategoriesResponse {
  categories: FishCategoryItem[];
}

export interface FishSpeciesDetail extends FishSpeciesSearchResult {
  diet: string | null;
  temp_min_f: number | null;
  temp_max_f: number | null;
  ph_min: number | null;
  ph_max: number | null;
  description: string | null;
}

export const CURRENCIES: { code: Currency; symbol: string; label: string }[] = [
  { code: 'USD', symbol: '$', label: 'US Dollar' },
  { code: 'GBP', symbol: '£', label: 'British Pound' },
  { code: 'EUR', symbol: '€', label: 'Euro' },
  { code: 'AUD', symbol: 'A$', label: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', label: 'Canadian Dollar' },
];

export const STATUS_OPTIONS: { value: LivestockStatus; label: string; color: string }[] = [
  { value: 'alive', label: 'Alive', color: 'green' },
  { value: 'deceased', label: 'Deceased', color: 'red' },
  { value: 'sold', label: 'Sold', color: 'blue' },
  { value: 'unknown', label: 'Unknown', color: 'gray' },
];

export const LIVESTOCK_TYPE_OPTIONS: { value: LivestockType; label: string }[] = [
  { value: 'fish', label: 'Fish' },
  { value: 'coral', label: 'Coral' },
  { value: 'invertebrate', label: 'Invertebrate' },
  { value: 'plant', label: 'Plant' },
];

export interface FishViewerEntry {
  canonical_id: number | null;
  common_name: string | null;
  scientific_name: string | null;
  category: string | null;
  source_count: number;
  // LiveAquaria source
  la_id: number | null;
  la_common_name: string | null;
  la_scientific_name: string | null;
  la_source_url: string | null;
  // AbyssAquatics source
  aa_id: number | null;
  aa_common_name: string | null;
  aa_scientific_name: string | null;
  aa_source_url: string | null;
  // TMC source
  tmc_id: number | null;
  tmc_common_name: string | null;
  tmc_scientific_name: string | null;
  tmc_source_url: string | null;
}

export interface FishViewerResponse {
  entries: FishViewerEntry[];
  total_count: number;
  query: string | null;
}

export interface FishViewerFilters {
  q?: string;
  category?: string;
  limit?: number;
  offset?: number;
  unmerged_only?: boolean;
}

// Source detail for merge/unmerge UI
export interface FishSourceDetail {
  id: number;
  source_name: string;
  common_name: string | null;
  scientific_name: string | null;
  family: string | null;
  category: string | null;
  care_level: string | null;
  temperament: string | null;
  diet: string | null;
  reef_compatible: string | null;
  max_size_inches: number | null;
  min_tank_size_gallons: number | null;
  description: string | null;
  image_url: string | null;
  source_url: string | null;
}

// Canonical with full source details for merge UI
export interface FishCanonicalWithSources {
  canonical_id: number;
  common_name: string | null;
  scientific_name: string | null;
  family: string | null;
  category: string | null;
  care_level: string | null;
  temperament: string | null;
  diet: string | null;
  reef_compatible: string | null;
  max_size_inches: number | null;
  min_tank_size_gallons: number | null;
  description: string | null;
  image_url: string | null;
  source_count: number;
  sources: FishSourceDetail[];
}

// Unmerge request/response
export interface UnmergeRequest {
  canonical_id: number;
  source_fish_id: number;
  field_selections: Record<string, string>;  // field -> "canonical" | source_name
}

export interface UnmergeResponse {
  new_canonical_id: number;
  message: string;
}

// Manual merge request/response
export interface ManualMergeRequest {
  source_canonical_id: number;  // Will be deleted after merge
  target_canonical_id: number;  // Will remain with combined data
  field_selections: Record<string, 'source' | 'target'>;
}

export interface ManualMergeResponse {
  merged_canonical_id: number;
  message: string;
}
